import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CajaSesion, CajaMovimiento, MetodoPago } from '@/types/database'

export type MetodoCaja = Exclude<MetodoPago, 'cuenta_corriente'>

export function useCajaActual(userId?: string) {
  return useQuery({
    queryKey: ['caja-actual', userId ?? 'any'],
    queryFn: () => api.get<(CajaSesion & { usuario_nombre?: string }) | null>('/api/caja/sesion-activa'),
    staleTime: 15_000,
  })
}

export function useCajaMovimientos(sesionId: string | null | undefined) {
  return useQuery({
    queryKey: ['caja-movimientos', sesionId],
    queryFn: () => api.get<CajaMovimiento[]>(`/api/caja/movimientos/${sesionId}`),
    enabled: !!sesionId,
    staleTime: 15_000,
  })
}

export function useAbrirCaja() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ montoApertura, nombreEmpleado }: { montoApertura: number; usuarioId: string; nombreEmpleado: string }) =>
      api.post<CajaSesion>('/api/caja/abrir', { monto_apertura: montoApertura, nombre_empleado: nombreEmpleado.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caja-actual'] })
    },
  })
}

export function useCerrarCaja() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sesionId, montoContado }: { sesionId: string; montoContado: number; montoCierre: number; diferencia: number }) =>
      api.put(`/api/caja/cerrar/${sesionId}`, { monto_contado: montoContado }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caja-actual'] })
      qc.invalidateQueries({ queryKey: ['caja-movimientos'] })
    },
  })
}

export function useAgregarMovimientoCaja() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sesionId, tipo, metodo, monto, concepto }: {
      sesionId: string
      tipo: 'ingreso' | 'egreso'
      metodo: MetodoCaja
      monto: number
      concepto: string
      usuarioId: string
    }) =>
      api.post('/api/caja/movimiento', { sesion_id: sesionId, tipo, metodo, monto, concepto }),
    onSuccess: (_data, { sesionId }) => {
      qc.invalidateQueries({ queryKey: ['caja-movimientos', sesionId] })
    },
  })
}

export function useCajaSesiones(limit = 30) {
  return useQuery({
    queryKey: ['caja-sesiones'],
    queryFn: () => api.get<(CajaSesion & { usuario_nombre?: string })[]>(`/api/caja/sesiones?limit=${limit}`),
    staleTime: 60_000,
  })
}
